import appConfig from "@config";
import {
  AlertCircle,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Flag,
  Infinity,
  Music,
  Pause,
  Play,
  RotateCcw,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { LogoFull } from "../layout/Logo";

interface AudioPlayerProps {
  mediaUrl?: string | null;
  title?: string;
  subtitle?: string;
  className?: string;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
}

const STORAGE_KEY_AUTOPLAY = "egarizim_audio_autoplay";
const STORAGE_KEY_AUTONEXT = "egarizim_audio_autonext";

export const AudioPlayer: React.FC<AudioPlayerProps> = ({
  mediaUrl,
  title,
  subtitle,
  className = "",
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasError, setHasError] = useState<boolean>(false);
  const [isAutoplayPending, setIsAutoplayPending] = useState<boolean>(false);

  // Normalisation de l'URL du média (force HTTPS pour éviter le blocage de contenu mixte en production)
  const safeMediaUrl = mediaUrl
    ? mediaUrl.replace(/^http:\/\//i, "https://")
    : null;

  // État de l'autoplay au chargement/changement de chant (par défaut: true)
  const [autoplay, setAutoplay] = useState<boolean>(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        const saved = localStorage.getItem(STORAGE_KEY_AUTOPLAY);
        if (saved !== null) {
          return saved === "true";
        }
      } catch (e) {
        console.warn("Failed to read autoplay setting from localStorage", e);
      }
    }
    return true;
  });

  // État de l'enchaînement automatique à la fin du chant (par défaut: false - désactivé)
  const [autoNext, setAutoNext] = useState<boolean>(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        const saved = localStorage.getItem(STORAGE_KEY_AUTONEXT);
        if (saved !== null) {
          return saved === "true";
        }
      } catch (e) {
        console.warn("Failed to read autoNext setting from localStorage", e);
      }
    }
    return false;
  });

  // Sauvegarde autoplay dans localStorage à chaque modification
  useEffect(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        localStorage.setItem(STORAGE_KEY_AUTOPLAY, String(autoplay));
      } catch (e) {
        console.warn("Failed to save autoplay setting to localStorage", e);
      }
    }
  }, [autoplay]);

  // Sauvegarde autoNext dans localStorage à chaque modification
  useEffect(() => {
    if (typeof window !== "undefined" && window.localStorage) {
      try {
        localStorage.setItem(STORAGE_KEY_AUTONEXT, String(autoNext));
      } catch (e) {
        console.warn("Failed to save autoNext setting to localStorage", e);
      }
    }
  }, [autoNext]);

  // État du formulaire de signalement
  const [isReportOpen, setIsReportOpen] = useState<boolean>(false);
  const [reportReason, setReportReason] = useState<string>(
    safeMediaUrl
      ? "L'audio ne démarre pas ou ne fonctionne pas"
      : "Audio manquant ou non disponible",
  );
  const [reportNotes, setReportNotes] = useState<string>("");
  const [isReportSubmitted, setIsReportSubmitted] = useState<boolean>(false);
  const [isSubmittingReport, setIsSubmittingReport] = useState<boolean>(false);
  const [reportError, setReportError] = useState<string>("");

  // Tentative de lecture sécurisée
  const attemptPlay = () => {
    if (!audioRef.current || !safeMediaUrl) return;
    setIsLoading(true);
    setHasError(false);
    const playPromise = audioRef.current.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          setIsPlaying(true);
          setIsLoading(false);
          setIsAutoplayPending(false);
        })
        .catch((err) => {
          console.warn("Playback attempt prevented by browser policy:", err);
          setIsPlaying(false);
          setIsLoading(false);
          // Si le navigateur bloque l'autoplay (ex: pas d'interaction préalable), on attend la 1ère interaction
          if (autoplay) {
            setIsAutoplayPending(true);
          }
        });
    }
  };

  // Récupération automatique dès la 1ère interaction de l'utilisateur (clic, touche, scroll, touch)
  useEffect(() => {
    if (!isAutoplayPending || !autoplay || isPlaying || !safeMediaUrl) {
      return;
    }

    const handleFirstUserGesture = () => {
      if (audioRef.current && !isPlaying) {
        audioRef.current
          .play()
          .then(() => {
            setIsPlaying(true);
            setIsLoading(false);
            setIsAutoplayPending(false);
          })
          .catch((err) => {
            console.warn("Play on first user gesture failed:", err);
          });
      }
    };

    const gestureEvents = ["click", "touchstart", "pointerdown", "keydown"];
    gestureEvents.forEach((evt) => {
      window.addEventListener(evt, handleFirstUserGesture, {
        once: true,
        passive: true,
      });
    });

    return () => {
      gestureEvents.forEach((evt) => {
        window.removeEventListener(evt, handleFirstUserGesture);
      });
    };
  }, [isAutoplayPending, autoplay, isPlaying, safeMediaUrl]);

  useEffect(() => {
    // Reset state when safeMediaUrl changes
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setHasError(false);
    setIsReportOpen(false);
    setIsReportSubmitted(false);
    setIsAutoplayPending(false);

    if (safeMediaUrl && autoplay) {
      const timer = setTimeout(() => {
        attemptPlay();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [safeMediaUrl, autoplay]);

  const handleCanPlay = () => {
    if (autoplay && !isPlaying && !hasError && audioRef.current) {
      if (audioRef.current.paused && !isAutoplayPending) {
        attemptPlay();
      }
    }
  };

  const toggleAutoplay = () => {
    setAutoplay((prev) => {
      const nextVal = !prev;
      if (nextVal) {
        toast.success("Lecture automatique au chargement activée");
        if (audioRef.current && safeMediaUrl && !isPlaying) {
          attemptPlay();
        }
      } else {
        setIsAutoplayPending(false);
        toast.info("Lecture automatique au chargement désactivée");
      }
      return nextVal;
    });
  };

  const toggleAutoNext = () => {
    setAutoNext((prev) => {
      const nextVal = !prev;
      if (nextVal) {
        toast.success(
          "Enchaînement automatique activé (passe au chant suivant)",
        );
      } else {
        toast.info("Enchaînement automatique désactivé");
      }
      return nextVal;
    });
  };

  const togglePlay = () => {
    if (!audioRef.current || !safeMediaUrl) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
      setIsAutoplayPending(false);
    } else {
      setIsAutoplayPending(false);
      attemptPlay();
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration || 0);
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    setCurrentTime(time);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
    }
  };

  const toggleMute = () => {
    if (audioRef.current) {
      audioRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const restartAudio = () => {
    if (audioRef.current && safeMediaUrl) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      if (!isPlaying) {
        attemptPlay();
      }
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (autoNext && onNext) {
      onNext();
    }
  };

  const formatTime = (secs: number) => {
    if (isNaN(secs) || secs === 0) return "0:00";
    const minutes = Math.floor(secs / 60);
    const seconds = Math.floor(secs % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;

  const handleSendReport = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmittingReport(true);
    setReportError("");

    try {
      const formData = new FormData();
      formData.append("access_key", appConfig.WEBFORMS3_EMAIL_KEY);
      formData.append(
        "subject",
        `[Signalement Audio Cantique] ${title || "Audio cantique"}`,
      );
      formData.append("from_name", "Signalement Audio Garizim");
      formData.append("cantique", title || "N/A");
      formData.append("recueil", subtitle || "N/A");
      formData.append("probleme_signale", reportReason);
      formData.append(
        "message",
        `Signalement audio pour le cantique : ${title || "Inconnu"}\n` +
          `Recueil : ${subtitle || "Inconnu"}\n` +
          `Problème sélectionné : ${reportReason}\n` +
          `Précisions de l'utilisateur : ${reportNotes || "Aucune précision"}\n` +
          `URL du fichier audio : ${safeMediaUrl || "Aucun audio (null)"}\n` +
          `Page active : ${window.location.href}`,
      );

      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        setIsReportSubmitted(true);
      } else {
        setReportError(
          data.message || "Erreur lors de l'envoi du signalement.",
        );
      }
    } catch (err) {
      console.error("Erreur lors de l'envoi du signalement audio:", err);
      setReportError(
        "Impossible d'envoyer le signalement actuellement. Veuillez réessayer.",
      );
    } finally {
      setIsSubmittingReport(false);
    }
  };

  return (
    <div
      className={`relative p-4 sm:p-5 rounded-2xl bg-slate-900/90 border border-slate-700/80 backdrop-blur-md shadow-2xl ${className}`}
    >
      {safeMediaUrl && (
        <audio
          ref={audioRef}
          src={safeMediaUrl}
          playsInline
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onCanPlay={handleCanPlay}
          onPlay={() => {
            setIsPlaying(true);
            setIsLoading(false);
            setIsAutoplayPending(false);
          }}
          onPlaying={() => {
            setIsPlaying(true);
            setIsLoading(false);
            setIsAutoplayPending(false);
          }}
          onPause={() => {
            setIsPlaying(false);
          }}
          onWaiting={() => {
            setIsLoading(true);
          }}
          onEnded={handleAudioEnded}
          onError={() => {
            setHasError(true);
            setIsLoading(false);
            setIsPlaying(false);
            setIsAutoplayPending(false);
          }}
          preload="auto"
        />
      )}

      <div className="flex flex-col gap-3">
        {/* Track Info & Action Badges */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-trx from-sky-900 to-slate-500 flex items-center justify-center shadow-lg shadow-sky-500/20 text-white flex-shrink-0">
              <Music className="w-5 h-5 transition-transform hover:scale-110" />
            </div>
            <div className="min-w-0">
              <h4 className="text-white font-bold text-sm sm:text-base truncate">
                <span className="text-amber-300 ">
                  {title || "Enregistrement Audio"}
                </span>
              </h4>
              {subtitle && (
                <p className="text-slate-400 text-xs sm:text-sm truncate">
                  {subtitle}
                </p>
              )}
            </div>
          </div>

          {/* Bouton de signalement */}
          <button
            type="button"
            onClick={() => {
              setIsReportOpen(true);
              setIsReportSubmitted(false);
            }}
            className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-rose-950/50 hover:bg-rose-900/60 text-rose-300 hover:text-rose-200 border border-rose-500/40 hover:border-rose-500/60 transition-colors shadow-sm cursor-pointer flex-shrink-0"
            title="Signaler un problème ou audio manquant"
          >
            <Flag className="w-3 h-3 text-rose-400" />
            <span className="hidden sm:inline">Signaler</span>
          </button>
        </div>

        {/* Audio controls or Unavailable audio message */}
        {safeMediaUrl ? (
          <>
            {/* Full-width Audio Progress Slider */}
            <div className="w-full flex flex-col gap-1.5 mt-1">
              <div className="relative w-full flex items-center">
                <input
                  type="range"
                  min="0"
                  max={duration || 100}
                  step="0.1"
                  value={currentTime}
                  onChange={handleSeek}
                  style={{
                    background: `linear-gradient(to right, #38bdf8 0%, #38bdf8 ${progressPercent}%, #334155 ${progressPercent}%, #334155 100%)`,
                  }}
                  className="w-full h-2.5 rounded-lg appearance-none cursor-pointer accent-sky-400 focus:outline-none transition-all"
                  aria-label="Progression du cantique"
                />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-400 font-mono px-0.5">
                <span>{formatTime(currentTime)}</span>
                <span>{formatTime(duration)}</span>
              </div>
            </div>

            {/* Playback Controls Harmonisés pour tous les écrans */}
            <div className="w-full max-w-sm sm:max-w-md mx-auto flex items-center justify-between sm:justify-center gap-1 sm:gap-2.5">
              {/* 1. Bouton Autoplay au chargement (Texte AUTO) */}
              <button
                type="button"
                onClick={toggleAutoplay}
                className={`px-1.5 sm:px-2.5 h-8 sm:h-9 rounded-xl border text-[10px] sm:text-xs font-black tracking-wider transition-all cursor-pointer flex items-center justify-center shrink-0 ${
                  autoplay
                    ? "bg-sky-500/20 text-sky-300 border-sky-500/50 shadow-sm shadow-sky-500/20 hover:bg-sky-500/30"
                    : "bg-slate-800/40 text-slate-500 border-slate-700/60 hover:text-slate-300 hover:bg-slate-800/80 hover:border-slate-600"
                }`}
                title={
                  autoplay
                    ? "Lecture automatique au chargement : activée - Cliquer pour désactiver"
                    : "Lecture automatique au chargement : désactivée - Cliquer pour activer"
                }
                aria-label="Activer ou désactiver la lecture automatique au chargement"
              >
                AUTO
              </button>

              {/* 2. Bouton Recommencer */}
              {false && (
                <button
                  type="button"
                  onClick={restartAudio}
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl border border-slate-700/60 bg-slate-800/40 text-slate-400 hover:text-white hover:bg-slate-800/80 hover:border-slate-600 transition-colors cursor-pointer flex items-center justify-center shrink-0"
                  title="Recommencer"
                  aria-label="Recommencer le cantique"
                >
                  <RotateCcw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                </button>
              )}

              {/* 3. Bouton Chant Précédent (<) */}
              {onPrevious !== undefined && (
                <button
                  type="button"
                  onClick={onPrevious}
                  disabled={hasPrevious === false}
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl border border-slate-700/60 bg-slate-800/40 text-slate-300 hover:text-white hover:bg-slate-800/80 hover:border-slate-600 disabled:opacity-25 disabled:cursor-not-allowed transition-all cursor-pointer flex items-center justify-center shrink-0"
                  title="Cantique précédent (<)"
                  aria-label="Cantique précédent"
                >
                  <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}

              {/* 4. Bouton Principal Lecture / Pause */}
              <button
                type="button"
                onClick={togglePlay}
                disabled={isLoading}
                className={`w-11 h-11 sm:w-13 sm:h-13 rounded-full bg-sky-500 hover:bg-sky-400 active:scale-95 text-slate-950 flex items-center justify-center shadow-lg shadow-sky-500/25 transition-all shrink-0 cursor-pointer mx-0.5 sm:mx-1 ${
                  isAutoplayPending
                    ? "ring-4 ring-sky-400/50 animate-pulse"
                    : ""
                }`}
                aria-label={isPlaying ? "Pause" : "Lecture"}
                title={
                  isAutoplayPending
                    ? "Cliquer pour démarrer la lecture"
                    : isPlaying
                      ? "Pause"
                      : "Lecture"
                }
              >
                {isLoading ? (
                  <span className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin"></span>
                ) : isPlaying ? (
                  <Pause className="w-5 h-5 sm:w-6 sm:h-6 fill-current" />
                ) : (
                  <Play className="w-5 h-5 sm:w-6 sm:h-6 fill-current ml-0.5" />
                )}
              </button>

              {/* 5. Bouton Chant Suivant (>) */}
              {onNext !== undefined && (
                <button
                  type="button"
                  onClick={onNext}
                  disabled={hasNext === false}
                  className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl border border-slate-700/60 bg-slate-800/40 text-slate-300 hover:text-white hover:bg-slate-800/80 hover:border-slate-600 disabled:opacity-25 disabled:cursor-not-allowed transition-all cursor-pointer flex items-center justify-center shrink-0"
                  title="Cantique suivant (>)"
                  aria-label="Cantique suivant"
                >
                  <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                </button>
              )}

              {/* 6. Bouton Muet / Volume */}
              <button
                type="button"
                onClick={toggleMute}
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl border border-slate-700/60 bg-slate-800/40 text-slate-400 hover:text-white hover:bg-slate-800/80 hover:border-slate-600 transition-colors shrink-0 cursor-pointer flex items-center justify-center"
                aria-label={isMuted ? "Activer le son" : "Désactiver le son"}
                title={isMuted ? "Activer le son" : "Couper le son"}
              >
                {isMuted ? (
                  <VolumeX className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-rose-400" />
                ) : (
                  <Volume2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                )}
              </button>

              {/* 7. Bouton Enchaînement Automatique (Lecture en continu - Symbole Infini) */}
              <button
                type="button"
                onClick={toggleAutoNext}
                className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl border transition-all cursor-pointer flex items-center justify-center shrink-0 ${
                  autoNext
                    ? "bg-amber-500/20 text-amber-300 border-amber-500/50 shadow-sm shadow-amber-500/20 hover:bg-amber-500/30"
                    : "bg-slate-800/40 text-slate-500 border-slate-700/60 hover:text-slate-300 hover:bg-slate-800/80 hover:border-slate-600"
                }`}
                title={
                  autoNext
                    ? "Lecture en continu activée (enchaîne sur le chant suivant) - Cliquer pour désactiver"
                    : "Lecture en continu désactivée - Cliquer pour activer"
                }
                aria-label="Activer ou désactiver la lecture en continu (chant suivant)"
              >
                <Infinity className="w-4 h-4 sm:w-4.5 sm:h-4.5" />
              </button>
            </div>

            {hasError && (
              <div className="flex items-center gap-2 text-rose-400 text-xs mt-1 bg-rose-950/40 p-2 rounded-lg border border-rose-900/50">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span>L'audio n'a pas pu être chargé.</span>
              </div>
            )}
          </>
        ) : (
          <div className="mt-1 p-3.5 sm:p-4 rounded-xl bg-slate-950/70 border border-slate-800/80 flex flex-col gap-3">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-lg bg-amber-500/15 border border-amber-500/30 text-amber-400 flex items-center justify-center shrink-0 mt-0.5">
                <VolumeX className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <p className="font-semibold text-slate-200 text-xs sm:text-sm">
                  Indisponible
                </p>
                <p className="text-slate-400 text-[11px] sm:text-xs mt-0.5 leading-relaxed">
                  L'enregistrement audio n'est pas encore disponible.
                </p>
              </div>
            </div>

            {(onPrevious !== undefined || onNext !== undefined) && (
              <div className="flex items-center justify-center gap-3 pt-2.5 border-t border-slate-800/60">
                {onPrevious !== undefined && (
                  <button
                    type="button"
                    onClick={onPrevious}
                    disabled={hasPrevious === false}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white text-xs font-semibold border border-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
                    title="Cantique précédent (<)"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Précédent</span>
                  </button>
                )}
                {onNext !== undefined && (
                  <button
                    type="button"
                    onClick={onNext}
                    disabled={hasNext === false}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800/80 hover:bg-slate-700 text-amber-300 hover:text-amber-200 text-xs font-semibold border border-slate-700 disabled:opacity-30 disabled:cursor-not-allowed transition-all cursor-pointer"
                    title="Cantique suivant (>)"
                  >
                    <span>Suivant</span>
                    <ChevronRight className="w-4 h-4" />
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de signalement audio monté dans document.body pour éviter tout problème de z-index ou de clipping */}
      {isReportOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md animate-in fade-in duration-200"
            onClick={() => !isSubmittingReport && setIsReportOpen(false)}
          >
            <div
              className="relative w-full max-w-md p-6 sm:p-7 rounded-3xl bg-slate-900 border border-slate-700/90 shadow-2xl text-left z-[100000]"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setIsReportOpen(false)}
                className="absolute top-5 right-5 p-2 rounded-full text-slate-400 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
                title="Fermer"
              >
                <X className="w-5 h-5" />
              </button>

              {!isReportSubmitted ? (
                <form onSubmit={handleSendReport} className="space-y-4">
                  <LogoFull style={{ filter: "invert(1)" }} />
                  <div className="flex items-center gap-2 text-rose-400 text-sm font-bold uppercase tracking-wider">
                    <Flag className="w-4 h-4" />
                    <span>Signaler</span>
                  </div>

                  <div className="mb-8">
                    <h3 className="text-lg font-bold text-white">
                      {title || "Ce cantique"}
                    </h3>
                    {/* <p className="text-xs text-slate-400 mt-0.5">
                      Aidez-nous à corriger les enregistrements audio
                      défectueux.
                    </p> */}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-slate-300 block">
                      Quel est le problème rencontré ?
                    </label>
                    {[
                      "Ce n'est pas le bon chant ou mauvais numéro",
                      "L'audio ne démarre pas ou ne fonctionne pas",
                      "Le son se coupe ou est inaudible",
                      "Autre problème",
                    ].map((reason) => (
                      <label
                        key={reason}
                        className="flex items-center gap-2.5 p-2.5 rounded-xl bg-slate-900/100 hover:bg-slate-800/80 border border-slate-800 text-xs text-slate-200 cursor-pointer transition-colors"
                      >
                        <input
                          type="radio"
                          name="reportReason"
                          value={reason}
                          checked={reportReason === reason}
                          onChange={(e) => setReportReason(e.target.value)}
                          className="accent-rose-500"
                        />
                        <span>{reason}</span>
                      </label>
                    ))}
                  </div>

                  <div>
                    <label className="text-sm font-semibold text-slate-300 block mb-1">
                      Précision facultative :
                    </label>
                    <textarea
                      value={reportNotes}
                      onChange={(e) => setReportNotes(e.target.value)}
                      placeholder="Ex: le son ne fonctionne pas/ce nest pas la bonne musique. Utiliser plutôt https://www.youtube.com/watch?v=<ID>..."
                      rows={8}
                      className="w-full p-2.5 rounded-xl bg-slate-900/100 border border-slate-700 text-xs text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-rose-500"
                    />
                  </div>

                  {reportError && (
                    <div className="p-2.5 rounded-xl bg-rose-950/60 border border-rose-800 text-rose-300 text-xs flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 flex-shrink-0" />
                      <span>{reportError}</span>
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setIsReportOpen(false)}
                      disabled={isSubmittingReport}
                      className="px-4 py-2 rounded-xl text-sm font-semibold text-slate-300 hover:text-white hover:bg-slate-800 transition-colors cursor-pointer"
                    >
                      Annuler
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmittingReport}
                      className="relative z-20 px-5 py-2.5 rounded-xl text-sm font-bold bg-rose-600 hover:bg-rose-500 active:scale-95 disabled:opacity-50 text-white shadow-lg shadow-rose-600/30 transition-all cursor-pointer flex items-center gap-2"
                    >
                      {isSubmittingReport ? (
                        <>
                          <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                          <span>Envoi en cours...</span>
                        </>
                      ) : (
                        <span>Envoyer</span>
                      )}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-center py-6 space-y-4">
                  <LogoFull style={{ filter: "invert(1)" }} />

                  <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto animate-in zoom-in-50 duration-300" />
                  <h3 className="text-xl font-bold text-white">
                    Merci pour votre signalement !
                  </h3>
                  <p className="text-xs text-slate-300 max-w-sm mx-auto leading-relaxed">
                    Votre retour a bien été enregistré. Notre équipe va vérifier
                    l'enregistrement audio de ce cantique.
                  </p>
                  <button
                    type="button"
                    onClick={() => setIsReportOpen(false)}
                    className="px-6 py-2 rounded-xl text-xs font-bold bg-sky-500 text-slate-950 hover:bg-sky-400 transition-colors shadow-lg cursor-pointer"
                  >
                    Fermer
                  </button>
                </div>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
